// Functional Widgets - Each widget is designed for specific use cases
export { TotalTrafficAreaChartWidget } from './TotalTrafficAreaChartWidget';
export { UserAcquisitionLineChartWidget } from './UserAcquisitionLineChartWidget';
export { OrganicClicksLineChartWidget } from './OrganicClicksLineChartWidget';
export { OrganicCTRNumberWidget } from './OrganicCTRNumberWidget';
export { AvgRankingPositionLineChartWidget } from './AvgRankingPositionLineChartWidget';
export { TopLandingPagesTableWidget } from './TopLandingPagesTableWidget';
export { TopGeoLocationsPieChartWidget } from './TopGeoLocationsPieChartWidget';
export { BounceRateNumberWidget } from './BounceRateNumberWidget';
export { AvgEngagementTimeLineChartWidget } from './AvgEngagementTimeLineChartWidget';
export { NewVsReturningUsersBarChartWidget } from './NewVsReturningUsersBarChartWidget';
export { DevicePerformancePieChartWidget } from './DevicePerformanceBarChartWidget';
export { BrowserPerformanceBarChartWidget } from './BrowserPerformanceBarChartWidget';
export { DevicesBrowsersPieChartWidget } from './DevicesBrowsersPieChartWidget';
export { ReferralWebsitesTableWidget } from './ReferralWebsitesTableWidget';
export { TopCountriesTableWidget } from './TopCountriesTableWidget';
export { TopReferralsTableWidget } from './TopReferralsTableWidget';
export { AvgEngagementTimeNumberWidget } from './AvgEngagementTimeNumberWidget';
export { DailyVisitApiWidget } from './DailyVisitApiWidget';

// Date utilities for all widgets
export interface DateFilteredEntry {
  dateKey: string;
  dayData: any;
}

/**
 * Filters data entries by date range
 * @param entries - Array of [dateKey, dayData] entries
 * @param report_start_date - Start date string (optional)
 * @param report_end_date - End date string (optional)
 * @returns Filtered entries within the date range
 */
export const filterDataByDateRange = (
  entries: [string, any][],
  report_start_date?: string,
  report_end_date?: string
): [string, any][] => {
  if (!report_start_date || !report_end_date) {
    return entries;
  }

  const startDate = new Date(report_start_date);
  const endDate = new Date(report_end_date);
  
  const filteredEntries = entries.filter(([dateKey, dayData]: [string, any]) => {
    if (!dayData || typeof dayData !== 'object') {
      return false;
    }
    
    // Parse the dateKey (format: "YYYYMMDD") instead of dayData.date
    if (dateKey.length !== 8) {
      return false;
    }
    
    const year = parseInt(dateKey.substring(0, 4));
    const month = parseInt(dateKey.substring(4, 6)) - 1; // Month is 0-indexed
    const day = parseInt(dateKey.substring(6, 8));
    const entryDate = new Date(year, month, day);
    
    return entryDate >= startDate && entryDate <= endDate;
  });
  
  console.log(`Date filtering: ${filteredEntries.length} entries from ${entries.length} total entries for date range ${report_start_date} to ${report_end_date}`);
  
  return filteredEntries;
};

/**
 * Validates that data has the expected structure with date-keyed entries
 * @param data - The raw data object
 * @returns Object with validation result and entries
 */
export const validateDateKeyedData = (data: any): { isValid: boolean; entries: [string, any][]; error?: string } => {
  if (!data || !data.data || typeof data.data !== 'object') {
    return { isValid: false, entries: [], error: 'Invalid data structure - missing data.data object' };
  }

  const rawData = data.data;
  const entries = Object.entries(rawData);
  
  if (entries.length === 0) {
    return { isValid: false, entries: [], error: 'No data entries found' };
  }

  // Check if first entry has the expected structure
  const firstEntry = entries[0];
  const [, firstDayData] = firstEntry;
  
  if (!firstDayData || typeof firstDayData !== 'object' || !(firstDayData as any).date) {
    return { isValid: false, entries: [], error: 'Invalid data format - entries must have date property' };
  }

  return { isValid: true, entries };
};

/**
 * Sorts chart data by date in ascending order
 * @param chartData - Array of chart data objects with date property
 * @returns Sorted chart data
 */
export const sortChartDataByDate = (chartData: any[]): any[] => {
  return chartData.sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

// Widget mapping for easy lookup by widget ID
export const FUNCTIONAL_WIDGETS = {
  'total-traffic': 'TotalTrafficAreaChartWidget',
  'user-acquisition': 'UserAcquisitionLineChartWidget',
  'organic-clicks': 'OrganicClicksLineChartWidget',
  'organic-ctr': 'OrganicCTRNumberWidget',
  'avg-ranking-position': 'AvgRankingPositionLineChartWidget',
  'top-landing-pages': 'TopLandingPagesTableWidget',
  'top-geo-locations': 'TopGeoLocationsPieChartWidget',
  'avg-engagement-time': 'AvgEngagementTimeLineChartWidget',
  'new-vs-returning-users': 'NewVsReturningUsersBarChartWidget',
  'device-performance': 'DevicePerformancePieChartWidget',
  'browser-performance': 'BrowserPerformanceBarChartWidget',
  'device-performance-pie': 'DevicePerformancePieChartWidget',
  'devices-browsers-pie': 'DevicesBrowsersPieChartWidget',
  'referral-websites': 'ReferralWebsitesTableWidget',
  'top-countries-table': 'TopCountriesTableWidget',
  'top-referrals-table': 'TopReferralsTableWidget',
  'bounce-rate': 'DailyVisitApiWidget',
  'avg-engagement-time-number': 'DailyVisitApiWidget',
  'total-page-views-count': 'DailyVisitApiWidget',
  'total-new-users-count': 'DailyVisitApiWidget',
} as const;
